import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Clock, ChevronRight, Sparkles } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppButton from "@/components/landing/WhatsAppButton";
import AuthorByline from "@/components/AuthorByline";
import NewsletterPopup from "@/components/NewsletterPopup";
import { Button } from "@/components/ui/button";
import { BLOG_POSTS } from "./posts";
import { ARTICLE_CONTENT } from "./articleContent";
import { ARTICLE_CONTENT_2 } from "./articleContent2";
import { ARTICLE_CONTENT_3 } from "./articleContent3";
import { ARTICLE_CONTENT_4 } from "./articleContent4";
import { ARTICLE_CONTENT_5 } from "./articleContent5";
import { ARTICLE_CONTENT_6 } from "./articleContent6";
import { ARTICLE_CONTENT_7 } from "./articleContent7";
import { getSEO } from "./seoMetadata";

const ALL_ARTICLES = {
  ...ARTICLE_CONTENT,
  ...ARTICLE_CONTENT_2,
  ...ARTICLE_CONTENT_3,
  ...ARTICLE_CONTENT_4,
  ...ARTICLE_CONTENT_5,
  ...ARTICLE_CONTENT_6,
  ...ARTICLE_CONTENT_7,
};

export default function BlogPost({ t, language, setLanguage }) {
  const { slug } = useParams();
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);
  const url = `https://elioralongevity.com/blog/${slug}`;
  const imageUrl = `https://elioralongevity.com/blog-images/${slug}.jpg`;
  const seo = getSEO(slug) || {};
  const seoTitle = seo.title || post.title;
  const seoDescription = seo.description || post.excerpt;
  const seoKeywords = seo.keywords || "";

  // Build JSON-LD: BlogPosting always + MedicalWebPage when condition is set
  const jsonLd = [{
    "@context": "https://schema.org",
    "@type": seo.condition ? "MedicalWebPage" : "BlogPosting",
    headline: post.title,
    name: seoTitle,
    description: seoDescription,
    datePublished: post.date,
    dateModified: post.date,
    inLanguage: "es-MX",
    keywords: seoKeywords,
    author: {
      "@type": "Person",
      name: "Sarai Núñez",
      url: "https://elioralongevity.com/sobre-sarai",
      jobTitle: "Distribuidora Independiente Immunotec",
    },
    publisher: {
      "@type": "Organization",
      name: "Eliora Longevity",
      logo: { "@type": "ImageObject", url: "https://elioralongevity.com/logo512.png" },
    },
    image: imageUrl,
    mainEntityOfPage: url,
    ...(seo.condition && {
      about: {
        "@type": "MedicalCondition",
        name: seo.condition.name,
        code: seo.condition.code && {
          "@type": "MedicalCode",
          code: seo.condition.code,
          codingSystem: "ICD-10",
        },
      },
      audience: {
        "@type": "MedicalAudience",
        audienceType: "Patient",
      },
      medicalAudience: ["Patient", "Caregiver"],
      lastReviewed: post.date,
      reviewedBy: {
        "@type": "Person",
        name: "Sarai Núñez",
      },
    }),
  }];

  return (
    <main className="relative bg-[#f9f6f0] text-[#1a2421]" data-testid="blog-post-page">
      <Helmet>
        <title>{`${seoTitle} · Eliora Longevity`}</title>
        <meta name="description" content={seoDescription} />
        {seoKeywords && <meta name="keywords" content={seoKeywords} />}
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:locale" content="es_MX" />
        <meta property="og:image" content={imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="article:author" content="Sarai Núñez" />
        <meta name="article:published_time" content={post.date} />
        <meta name="article:section" content={post.category} />
        <script type="application/ld+json">{JSON.stringify(jsonLd[0])}</script>
      </Helmet>

      <Header t={t} language={language} setLanguage={setLanguage} />

      <section className="border-b border-[#2c4a3b]/8 py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Link to="/blog" className="text-sm text-[#5c6b61] hover:text-[#bc6c25] inline-flex items-center gap-2 mb-6">
            <ArrowLeft className="w-4 h-4" /> Volver al blog
          </Link>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">{post.category}</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight">{post.title}</h1>
          <p className="mt-5 text-base md:text-lg text-[#5c6b61] leading-relaxed">{post.excerpt}</p>
          <p className="mt-6 text-xs text-[#5c6b61] inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {post.minutes} min de lectura
          </p>
        </div>
        <div className="max-w-4xl mx-auto px-6 md:px-10 mt-10">
          <div className="aspect-[16/9] rounded-3xl overflow-hidden border border-[#2c4a3b]/10 shadow-[0_30px_60px_-30px_rgba(26,36,33,0.35)]">
            <img
              src={`/blog-images/${slug}.jpg`}
              alt={post.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
            />
          </div>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <AuthorByline date={post.date} minutes={post.minutes} />
        <ArticleBody slug={slug} />

        <div className="mt-12 p-7 bg-white border border-[#2c4a3b]/8 rounded-3xl">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">¿Quieres tu plan personalizado?</p>
          <h3 className="font-serif text-2xl mt-2">Haz tu quiz wellness en 3 minutos</h3>
          <p className="text-sm text-[#5c6b61] mt-2">Y recibe un protocolo Immunotec de 3 meses adaptado a ti, gratis.</p>
          <Button asChild className="mt-4 rounded-full bg-[#2c4a3b] hover:bg-[#1e3328] text-white px-6 py-5">
            <Link to="/#quiz"><Sparkles className="w-4 h-4 mr-2" /> Empezar mi quiz</Link>
          </Button>
        </div>
      </article>

      <section className="bg-white border-t border-[#2c4a3b]/8 py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">Sigue leyendo</p>
          <h2 className="font-serif text-3xl mt-2">Artículos relacionados</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="group bg-[#f9f6f0] border border-[#2c4a3b]/8 rounded-2xl overflow-hidden hover:border-[#bc6c25]/40 transition">
                <div className="aspect-[16/10] overflow-hidden bg-[#e8e0d2]">
                  <img
                    src={`/blog-images/${r.slug}.jpg`}
                    alt={r.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-[#bc6c25] font-bold">{r.category}</p>
                  <h3 className="font-serif text-lg mt-2 group-hover:text-[#bc6c25]">{r.title}</h3>
                  <p className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-[#bc6c25] inline-flex items-center gap-1">
                    Leer <ChevronRight className="w-3 h-3" />
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer t={t} />
      <WhatsAppButton label={t?.whatsapp} />
      <NewsletterPopup />
    </main>
  );
}

/* eslint-disable react/no-unescaped-entities */
function ArticleBody({ slug }) {
  const body = ALL_ARTICLES[slug] || (
    <p className="text-[#5c6b61]">Este artículo está en preparación. Mientras tanto, te invitamos a leer nuestras <Link to="/blog" className="text-[#bc6c25] underline">otras publicaciones</Link>.</p>
  );

  return (
    <div className="prose-article">
      {body}
    </div>
  );
}
